<template>
    <div v-if="isLoaded" class="py-4 container-fluid">
        <!-- Dashboard Heading -->
        <div class="dashboard-heading mb-4">
            <div class="dh-icon-wrap dh-icon-volunteer">
                <i class="material-icons">volunteer_activism</i>
            </div>
            <div>
                <h5 class="dh-title">Volunteer Dashboard</h5>
                <p class="dh-subtitle">Manage your assignments, preferences, and availability</p>
            </div>
        </div>
        <nav class="volunteer-section-nav mb-3" aria-label="Volunteer dashboard sections">
            <span class="volunteer-section-nav-label">Jump to</span>
            <a v-if="hasVolunteerCard('userAvailability')" href="#volunteer-availability" @click.prevent="scrollToSection('volunteer-availability')">
                <i class="material-icons" aria-hidden="true">event_available</i>
                Availability
            </a>
            <a href="#volunteer-charts" @click.prevent="scrollToSection('volunteer-charts')">
                <i class="material-icons" aria-hidden="true">bar_chart</i>
                Charts
            </a>
            <a v-if="stSessionData?.personId && getLocationId()" href="#volunteer-people-groups" @click.prevent="scrollToSection('volunteer-people-groups')">
                <i class="material-icons" aria-hidden="true">family_restroom</i>
                People Groups
            </a>
            <a v-if="stSessionData?.personId" href="#volunteer-impact" @click.prevent="scrollToSection('volunteer-impact')">
                <i class="material-icons" aria-hidden="true">volunteer_activism</i>
                My Volunteer Impact
            </a>
            <a v-if="stSessionData?.personId && volunteerInvitesEnabled" href="#volunteer-invite" @click.prevent="scrollToSection('volunteer-invite')">
                <i class="material-icons" aria-hidden="true">mail</i>
                Invite someone
            </a>
            <a v-if="stSessionData?.showads === true" href="#volunteer-bible" @click.prevent="scrollToSection('volunteer-bible')">
                <i class="material-icons" aria-hidden="true">auto_stories</i>
                Bible Nerd Corner
            </a>
        </nav>
        <div v-if="hasVisibleVolunteerCards">
            <div class="row">
                <!-- first row cards -->
                <div
                    class="col-lg-3 col-md-6 col-sm-6 my-3"
                    v-for="id in firstRowVolunteerIds"
                    :key="id"
                >
                    <mini-statistics-card
                        :title="{
                            text: getVolunteerCard(id)?.label || '',
                            value: getVolunteerCardValue(id)
                        }"
                        @openModal="openModal"
                        :card="getVolunteerCard(id)"
                        :detail="getVolunteerCardDetail(id)"
                        :icon="getVolunteerCardIcon(id)"
                    />
                </div>
            </div>

            <div class="col-lg-12 my-2">
                <DashboardTable tableTitle="Volunteer Menu" :tableContent="filteredVolunteerMenuTable"
                    cardTitle="Upcoming Assignments" @view-page="openProjectModal"
                    :cardContent="filteredAssignments"
                    :summarylist="Array.isArray(stUserAssignments?.data['summary-list']) && stUserAssignments?.data['summary-list'].length > 0 ? stUserAssignments?.data['summary-list'] : null"
                    type="volunteer" :highlightRequestNewSkills="shouldHighlightRequestNewSkills" />
            </div>

            <br>
            <div id="volunteer-availability" class="row volunteer-section-anchor">
                <!-- second row cards -->
                <div
                    class="col-lg-3 col-md-6 col-sm-6 mb-2 mt-2"
                    v-for="id in secondRowVolunteerIds"
                    :key="id"
                >
                    <mini-statistics-card
                        :title="{
                            text: getVolunteerCard(id)?.label || '',
                            value: getVolunteerCardValue(id)
                        }"
                        @openModal="openModal"
                        :card="getVolunteerCard(id)"
                        :detail="getVolunteerCardDetail(id)"
                        :icon="getVolunteerCardIcon(id)"
                    />
                </div>
            </div>
        </div>

        <div id="volunteer-charts" class="row my-4 volunteer-section-anchor">
            <div class="col-lg-4 col-md-6 mt-5 mt-md-4">
                <chart-holder-card title="Confirmed Assignment count History"
                    subtitle="Count of assignments that were confirmed" update="confirmed assignment count updated">
                    <reports-line-chart id="tasks-chart" :chart="confirmedAssignmentCountChartData" />
                </chart-holder-card>
            </div>
            <div class="col-lg-4 col-md-6 mt-5 mt-md-4">
                <chart-holder-card title="Assignments count" subtitle="Total count of Assignments per month"
                    update="Total assignments per month updated" color="success">
                    <reports-line-chart id="tasks-chart-2" :chart="totalAssignmentChartData" />
                </chart-holder-card>
            </div>
            <div class="col-lg-4 mt-5 mt-md-4">
                <chart-holder-card title="Declined assignments count"
                    subtitle="Total count of assignments that were declined" update="just updated" color="dark">
                    <reports-line-chart id="tasks-chart-3" :chart="declinedAssignmentCountChartData" />
                </chart-holder-card>
            </div>
        </div>
        <div v-if="stSessionData?.personId && getLocationId()" id="volunteer-people-groups" class="row mb-4 volunteer-section-anchor">
            <div class="col-12">
                <PeopleGroupsPanel />
            </div>
        </div>
        <div v-if="stSessionData?.personId" id="volunteer-impact" class="row mb-4 volunteer-section-anchor">
            <div class="col-12">
                <VolunteerImpactCard
                    :person-id="stSessionData.personId"
                    :location-id="getLocationId()"
                />
            </div>
        </div>
        <div v-if="stSessionData?.personId" id="volunteer-invite" class="row mb-4 volunteer-section-anchor">
            <div class="col-12">
                <VolunteerInviteCard
                    :location-id="getLocationId()"
                    @feature-status="volunteerInvitesEnabled = $event"
                />
            </div>
        </div>
        <BibleNerdCorner
            v-if="stSessionData?.showads === true"
            id="volunteer-bible"
            :location-id="getLocationId()"
        />
        <Modal v-if="isModalOpen" :modalHeading="modalHeading" :modalContent="modalContent" :iframeUrl="iframeUrl"
             @close="closeModal" size="xl">
        </Modal>
        <ContentModal v-model="showAvailabilityModal" title="Availability" size="2xl" >
            <Availability :personId="stSessionData.personId" />
        </ContentModal>
        <ContentModal v-model="showSchedulePreferencesModal" title="Schedule Rules" size="2xl" >
            <SchedulePreferences />
        </ContentModal>
        <ContentModal v-model="showPreferredActivitiesModal" title="Preferred Activities" size="2xl" >
            <PreferredActivities />
        </ContentModal>
        <ContentModal v-model="showSignupFormModal" title="Volunteer Signup" size="2xl" >
            <SignupForm :locationID="getLocationId()" :userID="stSessionData.personId" />
        </ContentModal>
        <ContentModal v-model="showOpenPositionsModal" title="Sign-up Opportunities" size="2xl" >
            <nav class="modal-breadcrumb d-flex align-items-center flex-wrap gap-1 mb-3">
                <ol class="breadcrumb mb-0 d-flex align-items-center">
                    <li class="breadcrumb-item"><a href="#" class="crumb-link" @click.prevent="showOpenPositionsModal = false"><i class="material-icons crumb-icon">home</i> Volunteer Dashboard</a></li>
                    <li class="breadcrumb-item active"><span class="crumb-current"><i class="material-icons crumb-icon">leaderboard</i> Sign-up Opportunities</span></li>
                </ol>
            </nav>
            <OpenPositions :locationID="getLocationId()" :userID="stSessionData.personId" @open-interests="openServeInOtherAreas" />
        </ContentModal>
        <ContentModal v-model="showSignupInterestsModal" title="Serve in other Areas" size="2xl" >
            <nav class="modal-breadcrumb d-flex align-items-center flex-wrap gap-1 mb-3">
                <ol class="breadcrumb mb-0 d-flex align-items-center">
                    <li class="breadcrumb-item"><a href="#" class="crumb-link" @click.prevent="showSignupInterestsModal = false"><i class="material-icons crumb-icon">home</i> Volunteer Dashboard</a></li>
                    <li class="breadcrumb-item active"><span class="crumb-current"><i class="material-icons crumb-icon">explore</i> Serve in other Areas</span></li>
                </ol>
            </nav>
            <SignupInterests :locationID="getLocationId()" :userID="stSessionData.personId" />
        </ContentModal>
        <ContentModal v-model="showUserDetailsModal" title="My Profile" size="2xl" >
            <UserDetails
                :personId="stSessionData.personId"
                :locationId="getLocationId()"
                :self-profile="true"
                @account-action-completed="handleMyProfileAccountCompleted"
            />
        </ContentModal>
        <ContentModal v-model="showAllSchedulesModal" title="View All Schedules" size="2xl" >
            <AllSchedules />
        </ContentModal>
        <ContentModal v-model="showCurrentAssignedSkillsModal" title="My Assigned Skills" size="2xl" >
            <nav class="modal-breadcrumb d-flex align-items-center flex-wrap gap-1 mb-3">
                <ol class="breadcrumb mb-0 d-flex align-items-center">
                    <li class="breadcrumb-item"><a href="#" class="crumb-link" @click.prevent="showCurrentAssignedSkillsModal = false"><i class="material-icons crumb-icon">home</i> Volunteer Dashboard</a></li>
                    <li class="breadcrumb-item active"><span class="crumb-current"><i class="material-icons crumb-icon">workspace_premium</i> My Assigned Skills</span></li>
                </ol>
            </nav>
            <CurrentAssignedSkills :personId="stSessionData.personId" :isVolunteerView="true" @openServeInOtherAreas="openServeInOtherAreas" />
        </ContentModal>
        <ContentModal v-model="showNotificationsModal" title="Notification Preferences" size="2xl" >
            <NotificationsPreferences :personId="stSessionData.personId" :locationId="getLocationId()" />
        </ContentModal>
        <ContentModal v-model="showAvailableWorkgroupsModal" title="Available Workgroups' Teams" size="2xl" >
            <AvailableWorkgroups :personId="stSessionData.personId" :locationId="getLocationId()" />
        </ContentModal>

        <!-- Volunteer Summary Modal -->
        <VolunteerSummaryModal
            v-model="showVolunteerSummaryModal"
            personName=""
            :locationName="stSessionData?.locationName || ''"
            :prefetchedData="volunteerSummaryData"
            :personId="stSessionData.personId"
            :locationId="getLocationId()"
            :actionUris="volunteerActionUris"
            @navigate="handleVolunteerSummaryNav"
        />
    </div>

    <div v-else class="d-flex justify-content-center align-items-center h-full">
        <div>
            <p> <span class="loader"></span> </p>
        </div>
    </div>

</template>

<script>
import Modal from '@/components/Modal/Modal.vue';
import ContentModal from '@/components/Modal/ContentModal.vue';
// import TabCard from '@/components/Cards/TabCards.vue';
import { mapGetters, mapActions } from 'vuex';
import DashboardTable from './components/DashboardTable.vue';
import ChartHolderCard from "./components/ChartHolderCard.vue";
// import ReportsBarChart from "@/components/Charts/ReportsBarChart.vue";
import ReportsLineChart from "@/components/Charts/ReportsLineChart.vue";
import MiniStatisticsCard from './components/MiniStatisticsCard.vue';
import Availability from '@/modules/users/components/Availability.vue';
import SchedulePreferences from '@/modules/users/components/SchedulePreferences.vue';
import PreferredActivities from '@/modules/users/components/PreferredActivities.vue';
import SignupForm from '@/modules/signup-form/SignupForm.vue';
import OpenPositions from '@/modules/signup-form/components/OpenPositions.vue';
import SignupInterests from '@/modules/signup-form/components/SignupInterests.vue';
import UserDetails from '@/modules/users/components/UserDetails.vue';
import AllSchedules from '@/modules/all-schedules/AllSchedules.vue';
import CurrentAssignedSkills from '@/modules/users/components/CurrentAssignedSkills.vue';
import NotificationsPreferences from '@/modules/users/components/NotificationsPreferences.vue';
import AvailableWorkgroups from '@/modules/users/components/AvailableWorkgroups.vue';
import VolunteerSummaryModal from '@/components/VolunteerSummary/VolunteerSummaryModal.vue';
import VolunteerImpactCard from '@/components/VolunteerImpact/VolunteerImpactCard.vue';
import VolunteerInviteCard from '@/components/VolunteerInviteCard.vue';
import BibleNerdCorner from '@/components/BibleNerdCorner.vue';
import PeopleGroupsPanel from '@/modules/people-groups/PeopleGroupsPanel.vue';

import LoggerService from "@/services/LoggerService";
import { getLocationId as getLocationIdUtil } from "@/utils/SessionDataHandler";
import { baseUrl } from "@/utils/BaseUrl.js";
import api from '@/config/api';

export default {
    name: "usermenu",
    data() {
        return {
            isModalOpen: false,
            modalHeading: "",
            modalContent: "",
            iframeUrl: "",
            showAvailabilityModal: false,
            showSchedulePreferencesModal: false,
            showPreferredActivitiesModal: false,
            showSignupFormModal: false,
            showOpenPositionsModal: false,
            showSignupInterestsModal: false,
            showUserDetailsModal: false,
            skipUserDetailsRefreshOnClose: false,
            showAllSchedulesModal: false,
            showCurrentAssignedSkillsModal: false,
            showNotificationsModal: false,
            showAvailableWorkgroupsModal: false,
            showVolunteerSummaryModal: false,
            volunteerSummaryData: null,
            volunteerInvitesEnabled: false,
            mySkillsCount: 0,
            preferredActivitiesCount: 0,
            preferredWorkgroupsCount: 0,
            confirmedAssignmentCountChartData: {
                labels: [],
                datasets: {
                    label: 'Confirmed Assignments',
                    data: [],
                },
            },
            totalAssignmentChartData: {
                labels: [],
                datasets: {
                    label: 'Total Assignments',
                    data: [],
                },
            },
            declinedAssignmentCountChartData: {
                labels: [],
                datasets: {
                    label: 'Declined Assignments',
                    data: [],
                },
            },
            celebrationMessages: ["Yay!!!!", "Congratulations!!!", "Well Done!!", "Great Job!", "Fantastic!", "Amazing Work!", "You are a blessing!!!", "Awesome!!!!"],
            isLoaded: false,
            baseUrl: baseUrl
        };
    },
    components: {
        // TabCard,
        Modal,
        ContentModal,
        DashboardTable,
        ChartHolderCard,
        // ReportsBarChart,
        ReportsLineChart,
        MiniStatisticsCard,
        Availability,
        SchedulePreferences,
        PreferredActivities,
        SignupForm,
        OpenPositions,
        SignupInterests,
        UserDetails,
        AllSchedules,
        CurrentAssignedSkills,
        NotificationsPreferences,
        AvailableWorkgroups,
        VolunteerSummaryModal,
        VolunteerImpactCard,
        VolunteerInviteCard,
        BibleNerdCorner,
        PeopleGroupsPanel,
    },
    computed: {
        ...mapGetters([
            "stDashboardData",
            "stSessionData",
            "stMenuData",
            "stUserAssignments",
            "stAssignmentStatusCountByMonth",
            "stSignupCount",
            "stPersonAssignmentStatusCountByMonth",
            "stPermissionData",
            "stPersonPendingSwapRequests",
            "stPersonPreferences",
            "stVolunteerModules",
            "availabilityPagination",
            "totalRules",
            "getPersonSkills",
            "personSkillsPagination",
        ]),
        visibleVolunteerMenuCards() {
            const cards = this.stDashboardData?.volunteerMenuCards || [];
            const allowed = this.stPermissionData?.volunteerMenuCards;
            if (!allowed?.length) {
                return [];
            }
            return cards.filter((card) => allowed.includes(card?.id || card?.label));
        },
        hasVisibleVolunteerCards() {
            return this.visibleVolunteerMenuCards?.length > 0;
        },
        filteredVolunteerMenuTable() {
            const menu = this.stMenuData?.usermenu || [];
            const allowed = this.stPermissionData?.volunteerMenuCards;
            if (!allowed?.length) {
                return menu;
            }
            return menu.filter((item) => allowed.includes(item?.id || item?.label));
        },
        firstRowVolunteerIds() {
            return ['signUpAssignments', 'unconfirmedUpcomingAssignments', 'userPendingSwaps', 'myassignedskills'].filter((id) =>
                this.hasVolunteerCard(id)
            );
        },
        secondRowVolunteerIds() {
            return ['userAvailability', 'userRules', 'userPreferences', 'volunteerSummary'].filter((id) =>
                id === 'volunteerSummary' || this.hasVolunteerCard(id)
            );
        },
        volunteerMenuIds() {
            const menu = this.stMenuData?.usermenu || [];
            return menu.map((item) => item?.id);
        },
        unconfirmedDetail() {
            const count = this.stUserAssignments?.data?.summary?.pending_count || 0;
            if (count === 0) {
                const confirmed_count = this.stUserAssignments?.data?.summary?.upcoming_assignment_count || 0;
                const randomCelebrationMessage = this.getRandomCelebrationMessage();
                if (confirmed_count === 0) {
                    return `<span class='text-warning text-xs font-weight-bolder'>No upcoming assignments. </span>Please check back here for future assignments.`;
                } else {
                    return `<span class='text-success text-xs font-weight-bolder'>${randomCelebrationMessage} </span>You have confirmed all your upcoming assignments within the next 30 days.`;
                }
            } else {
                return `<span class='text-warning text-xs font-weight-bolder'>You have ${count} unconfirmed upcoming assignment(s) within the next 30 days.</span> Please respond to the assignment(s).`;
            }
        },
        signupDetail() {
            const count = this.stSignupCount?.signup_count || 0;
            if (count === 0) {
                return `<span class='text-success text-xs font-weight-bolder'></span>Please check back here for future sign-up opportunities.`;
            } else {
                return `<span class='text-warning text-xs font-weight-bolder'>There are sign-up opportunities available.</span> Please consider signing up for 1 or more open opportunities.`;
            }
        },
        swapDetail() {
            const count = this.stPersonPendingSwapRequests?.['response-records-count'] || 0;
            const requestCount = this.stPersonPendingSwapRequests?.['request-records-count'] || 0;
            if (count === 0) {
                if (requestCount > 0) {
                    return `<span class='text-success text-xs font-weight-bolder'> </span>You have submitted swap requests that are pending.`;
                } else {
                    return `<span class='text-success text-xs font-weight-bolder'> </span>You have no pending swap requests.`;
                }

            } else {
                return `<span class='text-warning text-xs font-weight-bolder'>Please respond to the pending swap request(s). </span>Your response will help the scheduling process to ensure coverage.`;
            }
        },
        preferenceDetail() {
            const count = this.preferredActivitiesCount + this.preferredWorkgroupsCount;
            if (count === 0) {
                return `<span class='text-warning text-xs font-weight-bolder'>No preferences selected. </span>This means you are available for ALL services. It is recommended that you specify preferred Activities and/or Workgroups if there are multiple Activities each week.`;
            } else {
                return `<span class='text-success text-xs font-weight-bolder'>You have selected Preferences. </span>You will only be scheduled for your selected preferences.`;
            }
        },
        rulesDetail() {
            const count = this.totalRules || 0;
            if (count === 0) {
                return `<span class='text-warning text-xs font-weight-bolder'>No Rules selected. </span>You could be scheduled for multiple services per week (if applicable). It is recommended that you add scheduling rule(s).`;
            } else {
                return `<span class='text-success text-xs font-weight-bolder'>You have specified Scheduling Rules. </span>The rules will be applied during automated schedule generation.`;
            }
        },
        availabilityDetail() {
            const count = this.availabilityPagination?.totalRecords || 0;
            console.log('count', count);
            
            if (count === 0) {
                return `<span class='text-warning text-xs font-weight-bolder'>No Availability records. </span>Use this option to specify vacations, schedule conflicts, etc.`;
            } else {
                return `<span class='text-success text-xs font-weight-bolder'>You have ${count} Availability record(s). </span>They will be evaluated during scheduling.`;
            }
        },
        volunteerActionUris() {
            // Build action URIs from the volunteer modules list
            const modules = this.stVolunteerModules || [];
            const uris = {};
            for (const m of modules) {
                if (['userPreferences', 'userAvailability', 'signUpAssignments'].includes(m.id)) {
                    uris[m.id] = m.id; // pass the module id; the navigate handler opens the matching modal
                }
            }
            return uris;
        },
        shouldHighlightRequestNewSkills() {
            // Check if user is NOT an admin in the location
            const isAdmin = this.stSessionData?.isAdminInLocation || false;
            if (isAdmin) {
                return false;
            }
            
            // Check if user has no skills
            const personSkills = this.getPersonSkills || [];
            const hasSkills = personSkills.length > 0;
            if (hasSkills) {
                return false;
            }
            
            // User is not admin AND has no skills - highlight the row
            return true;
        },
        // Filtered assignments with only required fields
        filteredAssignments() {
            const assignments = this.stUserAssignments?.data?.assignments;
            if (!Array.isArray(assignments)) {
                return [];
            }

            // console.log("assignments", assignments);
            
            return assignments.map(item => ({
                activityname: item.activityname,
                instancedate: item.instancedate,
                workgroupname: item.workgroupname,
                statusid: item.statusid,
                status: item.status,
                method: item.responsemethod,
            }));
        },

    },
    methods: {
        // In-page anchors resolve against <base href="/">, so scroll directly.
        scrollToSection(id) {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },

        ...mapActions([
            "fetchPersonAssignmentStatusCountByMonth",
            "fetchUserAssignments",
            "fetchSignupCount",
            "fetchPersonPendingSwapRequests",
            "fetchPersonPreferences",
            "fetchPersonAvailability",
            "fetchSchedulingPreferences",
        ]),

        getVolunteerCard(id) {
            if (id === 'volunteerSummary') return { id: 'volunteerSummary', label: 'My Volunteer Summary' };
            return this.visibleVolunteerMenuCards.find((card) => card?.id === id);
        },

        hasVolunteerCard(id) {
            return Boolean(this.getVolunteerCard(id));
        },

        getVolunteerCardValue(id) {
            switch (id) {
                case "signUpAssignments":
                    return this.stSignupCount?.signup_count || 0;
                case "unconfirmedUpcomingAssignments":
                    return this.stUserAssignments?.data?.summary?.pending_count || 0;
                case "userPendingSwaps":
                    return this.stPersonPendingSwapRequests?.["response-records-count"] || 0;
                case "userAvailability":
                    return this.availabilityPagination?.totalRecords || 0;
                case "userRules":
                    return this.totalRules || 0;
                case "userPreferences":
                    return this.preferredActivitiesCount + this.preferredWorkgroupsCount;
                case "myassignedskills":
                    return this.mySkillsCount;
                case "volunteerSummary": {
                    const pct = this.volunteerSummaryData?.setup_completeness?.percentage;
                    return pct != null ? `${pct}%` : '';
                }
                default:
                    return 0;
            }
        },

        getVolunteerCardDetail(id) {
            switch (id) {
                case "signUpAssignments":
                    return this.signupDetail;
                case "unconfirmedUpcomingAssignments":
                    return this.unconfirmedDetail;
                case "userPendingSwaps":
                    return this.swapDetail;
                case "userAvailability":
                    return this.availabilityDetail;
                case "userRules":
                    return this.rulesDetail;
                case "userPreferences":
                    return this.preferenceDetail;
                case "volunteerSummary": {
                    const sc = this.volunteerSummaryData?.setup_completeness;
                    if (sc) {
                        const configured = sc.configured_sections?.length || 0;
                        const total = configured + (sc.missing_sections?.length || 0);
                        const colorClass = sc.percentage >= 75 ? 'text-success' : sc.percentage >= 25 ? 'text-warning' : 'text-danger';
                        return `<span class='${colorClass} text-xs font-weight-bolder'>${configured} of ${total} key sections configured.</span> Click to view details.`;
                    }
                    return "<span class='text-xs'>View a summary of your volunteer setup and completeness</span>";
                }
                case "myassignedskills": {
                    const skillCount = this.mySkillsCount;
                    if (skillCount === 0) {
                        return `<span class='text-warning text-xs font-weight-bolder'>No assigned skills. </span>Contact your administrator to get skills assigned to you.`;
                    } else {
                        return `<span class='text-success text-xs font-weight-bolder'>You are assigned to ${skillCount} skill(s) in this location. </span>Click to view and manage your skill levels.`;
                    }
                }
                default:
                    return "";
            }
        },

        getVolunteerCardIcon(id) {
            const icons = {
                signUpAssignments: { name: "leaderboard", color: "text-white", background: "info" },
                unconfirmedUpcomingAssignments: { name: "person", color: "text-white", background: "success" },
                userPendingSwaps: { name: "weekend", color: "text-white", background: "dark" },
                userAvailability: { name: "calendar_today", color: "text-white", background: "warning" },
                userRules: { name: "rule", color: "text-white", background: "info" },
                userPreferences: { name: "favorite", color: "text-white", background: "danger" },
                myassignedskills: { name: "workspace_premium", color: "text-white", background: "danger" },
                volunteerSummary: { name: "summarize", color: "text-white", background: "primary" },
            };
            return icons[id] || { name: "info", color: "text-white", background: "secondary" };
        },

        getLocationId() {
            return getLocationIdUtil();
        },
        async handleMyProfileAccountCompleted(result) {
            if (!result?.shouldLeaveEditor) return;

            this.skipUserDetailsRefreshOnClose = true;
            this.showUserDetailsModal = false;

            if (result.data?.account_deleted) {
                this.$store.commit("LOGOUT");
                this.$store.commit("CLEAR_SESSION_DATA");
                this.$store.commit("SET_SIDEBAR_ROUTES", null);
                localStorage.clear();
                await this.$router.replace({ name: "SignIn" });
                return;
            }

            if (
                result.scope !== "location"
                || !["delete", "notify_and_delete"].includes(result.resolution)
            ) {
                return;
            }

            const personId = Number(this.stSessionData?.personId);
            const remainingLocations = Array.isArray(result.data?.remaining_locations)
                ? result.data.remaining_locations
                : [];
            const nextLocation = remainingLocations.find(
                (location) => Number(location.locationid) > 0
            );

            if (!nextLocation) {
                const sessionData = { ...(this.stSessionData || {}) };
                sessionData.locationid = null;
                sessionData.organizationid = null;
                delete sessionData.locationId;
                this.$store.commit("SET_SESSION_DATA", sessionData);
                this.$store.commit("SET_SIDEBAR_ROUTES", null);
                localStorage.removeItem("apiCache_getsessiondetails");
                if (personId > 0) {
                    await this.$store.dispatch("fetchPersonLocations", { personid: personId });
                }
                await this.$router.replace({ name: "setup" });
                return;
            }

            const nextLocationId = String(nextLocation.locationid);
            await this.$store.dispatch("setPersonLocations", nextLocationId);
            localStorage.removeItem("apiCache_getsessiondetails");
            await this.$store.dispatch("fetchSessionData");

            const sessionData = {
                ...(this.$store.getters.stSessionData || this.stSessionData || {}),
                locationid: nextLocationId,
            };
            delete sessionData.locationId;
            this.$store.commit("SET_SESSION_DATA", sessionData);

            if (personId > 0) {
                await this.$store.dispatch("fetchPersonLocations", { personid: personId });
            }
            const storedLocationGroups = this.$store.getters.stLocations || [];
            const locationGroups = Array.isArray(storedLocationGroups)
                ? storedLocationGroups
                : [storedLocationGroups];
            const locationGroup = locationGroups.find((organization) => (
                organization.locations?.some(
                    (location) => String(location.location_id) === nextLocationId
                )
            ));
            sessionData.organizationid = locationGroup?.organization_id ?? null;
            this.$store.commit("SET_SESSION_DATA", sessionData);
            this.$store.commit("SET_SIDEBAR_ROUTES", null);

            const sidebarRoutes = await this.$store.dispatch("fetchSidebarRoutes");
            const defaultRoute = sidebarRoutes?.defaultRoute
                ? `/${String(sidebarRoutes.defaultRoute).replace(/^\/+/, "")}`
                : sessionData.isAdminInLocation ? "/admin" : "/volunteer";
            await this.$router.replace(defaultRoute);
            window.location.reload();
        },

        openModal(item) {
            // Volunteer Summary card
            if (item?.id === "volunteerSummary") {
                this.showVolunteerSummaryModal = true;
                LoggerService.logInfo(`opening volunteer summary modal in UserMenu.vue`);
                return;
            }

            // Check if this is the signup card - open OpenPositions modal
            if (item?.id === "signUpAssignments") {
                this.showOpenPositionsModal = true;
                LoggerService.logInfo(`opening open positions modal in UserMenu.vue`);
                return;
            }

            // Check if this is the availability card
            if (item?.id === "userAvailability") {
                this.showAvailabilityModal = true;
                LoggerService.logInfo(`opening availability modal in UserMenu.vue`);
                return;
            }

            // Check if this is the schedule rules card
            if (item?.id === "userRules") {
                this.showSchedulePreferencesModal = true;
                LoggerService.logInfo(`opening schedule rules modal in UserMenu.vue`);
                return;
            }

            // Check if this is the pending swaps card - open iframe with show_chart=0
            if (item?.id === "userPendingSwaps") {
                const locationId = getLocationIdUtil();
                let uri = item.uri.replace('<locationid>', locationId).replace('<personid>', this.stSessionData.personId);
                // Append show_chart=0 parameter
                uri += (uri.includes('?') ? '&' : '?') + 'show_chart=0';
                this.iframeUrl = uri;
                this.modalHeading = item.label;
                this.isModalOpen = true;
                LoggerService.logInfo(`opening modal [${item.label}] for url [${this.iframeUrl}] in UserMenu.vue`);
                return;
            }

            // Check if this is the unconfirmed assignments card - open iframe with show_chart=0 and status_list=pending
            if (item?.id === "unconfirmedUpcomingAssignments") {
                const locationId = getLocationIdUtil();
                let uri = item.uri.replace('<locationid>', locationId).replace('<personid>', this.stSessionData.personId);
                // Append show_chart=0 and status_list=pending parameters
                uri += (uri.includes('?') ? '&' : '?') + 'show_chart=0&status_list=pending';
                this.iframeUrl = uri;
                this.modalHeading = item.label;
                this.isModalOpen = true;
                LoggerService.logInfo(`opening modal [${item.label}] for url [${this.iframeUrl}] in UserMenu.vue`);
                return;
            }

            // Check if this is the preferred activities card
            if (item?.id === "userPreferences") {
                this.showPreferredActivitiesModal = true;
                LoggerService.logInfo(`opening preferred activities modal in UserMenu.vue`);
                return;
            }

            // Check if this is the assigned skills card
            if (item?.id === "myassignedskills") {
                this.showCurrentAssignedSkillsModal = true;
                LoggerService.logInfo(`opening current assigned skills modal in UserMenu.vue`);
                return;
            }

            // For other cards, use iframe modal
            this.modalHeading = item.label
            const locationId = getLocationIdUtil();            
            this.iframeUrl = item.uri.replace('<locationid>', locationId).replace('<personid>', this.stSessionData.personId);
            this.isModalOpen = true;
            LoggerService.logInfo(`opening modal [${item.label}] for url [${this.iframeUrl}] in UserMenu.vue`);
        },
        openPage(item) {
            const locationId = getLocationIdUtil();
            return item.replace('<locationid>', locationId).replace('<personid>', this.stSessionData.personId);
        },
        closeModal() {
            this.isModalOpen = false;
            this.getCountValues();
        },
        // openProjectModal({ label, uri }) {
        //     this.iframeUrl = uri;
        //     this.modalHeading = label;
        //     this.isModalOpen = true;
        //     LoggerService.logInfo(`opening project modal [${label}] for uri [${uri}] in UserMenu.vue`);
        // },
        openProjectModal({ label, uri, uri_type, id }) {
            // Volunteer Summary — open modal instead of navigating
            if (id === "volunteerSummary") {
                this.showVolunteerSummaryModal = true;
                LoggerService.logInfo(`opening volunteer summary modal from menu in UserMenu.vue`);
                return;
            }

            // Check if this is the myProfile module - open UserDetails modal
            if (id === "myProfile") {
                this.showUserDetailsModal = true;
                LoggerService.logInfo(`opening user details modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the signUpAssignments module - open OpenPositions modal
            if (id === "signUpAssignments") {
                this.showOpenPositionsModal = true;
                LoggerService.logInfo(`opening open positions modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the availability module - open Availability modal
            if (id === "userAvailability") {
                this.showAvailabilityModal = true;
                LoggerService.logInfo(`opening availability modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the scheduling rules module - open Schedule Rules modal
            if (id === "userRules") {
                this.showSchedulePreferencesModal = true;
                LoggerService.logInfo(`opening schedule rules modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the preferences module - open Preferred Activities modal
            if (id === "userPreferences") {
                this.showPreferredActivitiesModal = true;
                LoggerService.logInfo(`opening preferred activities modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the requestNewSkills module - open SignupInterests modal
            if (id === "requestNewSkills") {
                this.showSignupInterestsModal = true;
                LoggerService.logInfo(`opening signup interests modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the allschedules module - open AllSchedules modal
            if (id === "allschedules") {
                this.showAllSchedulesModal = true;
                LoggerService.logInfo(`opening all schedules modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the myassignedskills module - open CurrentAssignedSkills modal
            if (id === "myassignedskills") {
                this.showCurrentAssignedSkillsModal = true;
                LoggerService.logInfo(`opening current assigned skills modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the notification preferences module
            if (id === "userNotifications") {
                this.showNotificationsModal = true;
                LoggerService.logInfo(`opening notification preferences modal in UserMenu.vue`);
                return;
            }
            
            // Check if this is the available workgroup teams module
            if (id === "userTeams") {
                this.showAvailableWorkgroupsModal = true;
                LoggerService.logInfo(`opening available workgroups modal in UserMenu.vue`);
                return;
            }
            
            // For other modules, use iframe modal if uri_type is modal, otherwise navigate
            if (uri_type === "modal") {
                this.iframeUrl = uri;
                this.modalHeading = label;
                this.isModalOpen = true;
                LoggerService.logInfo(`opening iframe modal [${label}] for url [${uri}] in UserMenu.vue`);
            } else {
                window.location.href = uri;
                LoggerService.logInfo(`navigating to [${label}] for url [${uri}] in UserMenu.vue`);
            }
        },
        handleVolunteerSummaryNav(moduleId) {
            this.showVolunteerSummaryModal = false;
            if (moduleId === 'userPreferences') {
                this.showPreferredActivitiesModal = true;
            } else if (moduleId === 'userAvailability') {
                this.showAvailabilityModal = true;
            } else if (moduleId === 'signUpAssignments') {
                this.showOpenPositionsModal = true;
            }
        },
        async fetchVolunteerSummary() {
            try {
                const locationId = getLocationIdUtil();
                const response = await api.get('/auth/person-volunteer-summary', {
                    params: { personid: this.stSessionData.personId, locationid: locationId },
                });
                this.volunteerSummaryData = response.data?.volunteer_summary || response.data || null;
            } catch (err) {
                console.error('Error prefetching volunteer summary:', err);
            }
        },
        openServeInOtherAreas() {
            this.showCurrentAssignedSkillsModal = false;
            this.showOpenPositionsModal = false;
            this.showSignupInterestsModal = true;
            LoggerService.logInfo(`opening serve in other areas modal from current assigned skills in UserMenu.vue`);
        },
        getMonthIndex(month) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return months.indexOf(month);
        },
        getRandomCelebrationMessage() {
            const randomIndex = Math.floor(Math.random() * this.celebrationMessages.length);
            return this.celebrationMessages[randomIndex];
        },

        prepareAssignmentsChartData() {
            // Early return if data is not valid
            if (!Array.isArray(this.stPersonAssignmentStatusCountByMonth) || this.stPersonAssignmentStatusCountByMonth.length === 0) {
                console.log("exiting prepareAssignmentsChartData as data is not valid");
                console.log(this.stPersonAssignmentStatusCountByMonth);
                return;
            }

            // Use labels directly from API data (already sorted chronologically, may include year suffix e.g. "Jun 25")
            const labels = [];
            const confirmedData = [];
            const declinedData = [];
            const totalData = [];

            this.stPersonAssignmentStatusCountByMonth.forEach((entry) => {
                labels.push(entry.month);
                confirmedData.push(parseInt(entry.confirmed_count || 0, 10));
                declinedData.push(parseInt(entry.declined_count || 0, 10));
                totalData.push(parseInt(entry.total_assigned_count || 0, 10));
            });

            this.confirmedAssignmentCountChartData.labels = labels;
            this.confirmedAssignmentCountChartData.datasets.data = confirmedData;

            this.declinedAssignmentCountChartData.labels = labels;
            this.declinedAssignmentCountChartData.datasets.data = declinedData;

            this.totalAssignmentChartData.labels = labels;
            this.totalAssignmentChartData.datasets.data = totalData;
        },


        async getCountValues() {
            try {
                const locationId = getLocationIdUtil();
                const personId = this.stSessionData?.personId;
                // Guard: skip API calls if session data is missing (e.g. during logout)
                if (!personId || !locationId) {
                    console.log('getCountValues: skipping - missing personId or locationId');
                    return;
                }
                await this.fetchUserAssignments({ personid: personId, locationid: locationId, limit: 5 })
                await this.fetchSignupCount({ personid: personId, locationid: locationId })
                await this.fetchPersonPendingSwapRequests({ personid: personId, locationid: locationId })
                const personPrefsResponse = await this.fetchPersonPreferences({ personId, locationId })
                // Extract preferred activities/workgroups counts from person preferences
                const prefs = personPrefsResponse?.preferences;
                this.preferredActivitiesCount = prefs?.activity_preferences?.preferred_activities?.length || 0;
                this.preferredWorkgroupsCount = prefs?.workgroup_preferences?.preferred_workgroups?.length || 0;
                await this.fetchPersonAvailability({ 
                    personId, 
                    locationId,
                    page: 1,
                    pageSize: 10
                })
                await this.fetchSchedulingPreferences({
                    personId,
                    locationId
                })
                await this.fetchPersonAssignmentStatusCountByMonth({
                    personid: personId,
                    locationid: locationId
                });
                // Fetch person skills to check if user has any skills and get count
                await this.$store.dispatch("fetchPersonSkills", {
                    personId,
                    locationId,
                    page: 1,
                    pageSize: 10, // Get first page of skills for the card
                });
                
                // Save skills count to local state to prevent it from being overwritten
                this.mySkillsCount = this.personSkillsPagination?.totalRecords || 0;

                // Fetch volunteer summary for card percentage (fire-and-forget, non-blocking)
                this.fetchVolunteerSummary();

                this.prepareAssignmentsChartData();
                this.isLoaded = true;
            } catch (error) {
                console.error("Error fetching count values:", error);
            }
        }


    },
    watch: {
        showAvailabilityModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showSchedulePreferencesModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showPreferredActivitiesModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showSignupFormModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showOpenPositionsModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showSignupInterestsModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showUserDetailsModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) {
                if (this.skipUserDetailsRefreshOnClose) {
                    this.skipUserDetailsRefreshOnClose = false;
                    return;
                }
                this.getCountValues();
            }
        },
        showAllSchedulesModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showCurrentAssignedSkillsModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showNotificationsModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showAvailableWorkgroupsModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.getCountValues();
        },
        showVolunteerSummaryModal(newVal, oldVal) {
            if (oldVal === true && newVal === false) this.fetchVolunteerSummary();
        },
    },
    mounted() {
        this.getCountValues()
    }
};
</script>

<style scoped>
.loader {
    width: 48px;
    height: 48px;
    border: 5px solid green;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* Modal breadcrumb styles (matches BreadcrumbNav) */
.modal-breadcrumb .breadcrumb {
    background: none;
    padding: 0;
    list-style: none;
    font-size: 0.82rem;
}
.modal-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
    content: "";
    display: inline-block;
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 6px solid #adb5bd;
    margin: 0 0.45rem;
    vertical-align: middle;
}
.modal-breadcrumb .crumb-link {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}
.modal-breadcrumb .crumb-link:hover {
    color: #4c63d2;
    text-decoration: underline;
}
.modal-breadcrumb .crumb-current {
    color: #344767;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}
.modal-breadcrumb .crumb-icon {
    font-size: 16px;
    vertical-align: middle;
}
/* Dashboard heading */
.dashboard-heading {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.dh-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
}
.dh-icon-wrap i { font-size: 28px; }
.dh-icon-volunteer { background: linear-gradient(135deg, #66bb6a, #43a047); }
.dh-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #344767;
}
.dh-subtitle {
    margin: 0;
    font-size: 0.78rem;
    color: #7b809a;
    margin-top: 2px;
}
.volunteer-section-nav {
    position: sticky;
    top: 5rem;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    overflow-x: auto;
    padding: 0.45rem 0.6rem;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(52, 71, 103, 0.1);
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(52, 71, 103, 0.06);
    scrollbar-width: thin;
}
.volunteer-section-nav-label {
    flex: 0 0 auto;
    padding: 0 0.45rem 0 0.2rem;
    color: #7b809a;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
}
.volunteer-section-nav a {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.35rem;
    padding: 0.38rem 0.58rem;
    color: #526078;
    font-size: 0.76rem;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    border-radius: 4px;
    transition: color 0.15s ease, background-color 0.15s ease;
}
.volunteer-section-nav a:hover,
.volunteer-section-nav a:focus-visible {
    color: #2e7d32;
    background: #edf7ee;
    outline: none;
}
.volunteer-section-nav a .material-icons {
    font-size: 16px;
}
.volunteer-section-anchor {
    scroll-margin-top: 7rem;
}
@media (prefers-reduced-motion: no-preference) {
    :global(html) {
        scroll-behavior: smooth;
    }
}
@media (max-width: 575.98px) {
    .volunteer-section-nav {
        top: 4rem;
        margin-right: -0.25rem;
        margin-left: -0.25rem;
    }
}
</style>
